import type { Worksheet } from "exceljs";
import { CALL_OFF_SOURCE_LABEL_ENTRIES, resolveCallInTime } from "../../../utils/callIns";
import { formatPersonFullName } from "../../../utils/formatters";
import type { CalledOffShift, ChangesSource, HospitalData } from "../../../utils/models";
import { MISSING_SHIFT_PLACEHOLDER } from "../constants";
import type { PrintoutColumn } from "../types";
import { formatTimeTo12Hour } from "../util/shiftTimeFormat";
import { COLUMN_HEADER_FILL, SECTION_HEADER_FILL, THIN_BORDER } from "./cellStyle";

interface RenderCallOutsSectionArgs {
	worksheet: Worksheet;
	currentRow: number;
	callOutShifts: CalledOffShift[];
	hospitalData: HospitalData;
	timezone: string;
	sortBy: PrintoutColumn[] | undefined;
	lastNameFirst: boolean;
}

/** Fixed column shape — doesn't map onto `PrintoutColumnType`, so it's not user-configurable. */
const CALL_OUTS_COLUMN_HEADERS = ["Shift Start", "Shift End", "Nurse Name", "Called At", "Reason", "Source", "Notes"] as const;

/**
 * English label for a call-off source, from the same canonical table `useCallOffSourceLabel` uses
 * for its translated labels — kept in one shared place (`utils/callIns.ts`) so the two can't drift
 * on WHICH sources are specially labeled. Hardcoded English here (not translated) because this
 * export pipeline doesn't run i18n at all — see this section's hardcoded "Call Outs" title below.
 * Falls back to the raw enum value, same as `useCallOffSourceLabel`.
 */
const resolveCallOffSourceLabel = (source: ChangesSource | undefined): string => {
	if (!source) return "";
	return CALL_OFF_SOURCE_LABEL_ENTRIES.find((e) => e.source === source)?.english ?? source;
};

/** Same name format as `fetchPtoDeletedShifts` (honours the department's name-order flag). */
const resolveNurseName = (callOut: CalledOffShift, hospitalData: HospitalData, lastNameFirst: boolean): string => {
	const nurse = hospitalData.nurses.find((n) => n.id === callOut.nurseId);
	return nurse ? formatPersonFullName(nurse, lastNameFirst) : MISSING_SHIFT_PLACEHOLDER;
};

/**
 * Re-sort call-out rows by the FIRST `sortBy` entry whose column kind has an equivalent on a
 * call-out row — `"name"` or `"shiftTime"`, honoring that entry's `sortDirection`. Every other
 * printout column kind (certification, location, role, floor, …) has no meaning for a call-out row
 * and is ignored. Falls back to the hook's own default order (shift start ascending) when no
 * matching entry exists.
 */
export const sortCallOuts = (
	callOutShifts: CalledOffShift[],
	sortBy: PrintoutColumn[] | undefined,
	hospitalData: HospitalData,
	lastNameFirst: boolean,
): CalledOffShift[] => {
	// Only enabled columns participate — same convention as `fromConfiguredSortBy` in `sort/sourceSelector.ts`.
	const sortEntry = sortBy?.filter((col) => col.enabled).find((col) => col.type === "name" || col.type === "shiftTime");
	if (!sortEntry) return callOutShifts;

	const direction = sortEntry.sortDirection === "desc" ? -1 : 1;
	const sorted = [...callOutShifts];
	if (sortEntry.type === "name") {
		sorted.sort((a, b) => direction * resolveNurseName(a, hospitalData, lastNameFirst).localeCompare(resolveNurseName(b, hospitalData, lastNameFirst)));
	} else {
		sorted.sort((a, b) => direction * (a.startTime.toMillis() - b.startTime.toMillis()));
	}
	return sorted;
};

/**
 * Render the "Call Outs" section at the bottom of a sheet.
 *
 * Structurally simpler than `renderPtoSection` — a flat list, no `printoutGroupBy` sub-grouping —
 * because the fixed 7-column shape the user requested doesn't map onto the configurable
 * `PrintoutColumn` system PTO reuses. Hand-rolls header/row emission with the same `cellStyle`
 * building blocks (fonts, fills, borders) the `PrintoutColumn`-driven helpers use, for visual
 * consistency with the rest of the export.
 *
 * Layout:
 * - One spacer row
 * - Bold "Call Outs" title row (hardcoded English — matches the pipeline's existing convention of
 *   not running i18n through the export pipeline; see "Time Off / PTO" in `ptoSection.ts`)
 * - Column-header row
 * - One row per call-out, in `sortCallOuts` order
 *
 * Returns the row after the section, or the input row unchanged if there are no call-outs.
 */
export const renderCallOutsSection = (args: RenderCallOutsSectionArgs): number => {
	const { worksheet, currentRow: initialRow, callOutShifts, hospitalData, timezone, sortBy, lastNameFirst } = args;

	if (callOutShifts.length === 0) return initialRow;

	const totalColumns = CALL_OUTS_COLUMN_HEADERS.length;
	const lastColumnLetter = String.fromCharCode("A".charCodeAt(0) + totalColumns - 1);

	let currentRow = initialRow + 1;

	const titleCell = worksheet.getCell(`A${currentRow}`);
	titleCell.value = "Call Outs";
	titleCell.font = { bold: true, size: 14, color: { argb: "FF000000" } };
	titleCell.fill = SECTION_HEADER_FILL;
	titleCell.alignment = { horizontal: "center" };
	worksheet.mergeCells(`A${currentRow}:${lastColumnLetter}${currentRow}`);
	currentRow += 1;

	CALL_OUTS_COLUMN_HEADERS.forEach((header, index) => {
		const cell = worksheet.getCell(currentRow, index + 1);
		cell.value = header;
		cell.font = { bold: true };
		cell.fill = COLUMN_HEADER_FILL;
		cell.border = THIN_BORDER;
	});
	currentRow += 1;

	const sortedCallOuts = sortCallOuts(callOutShifts, sortBy, hospitalData, lastNameFirst);

	for (const callOut of sortedCallOuts) {
		const excelRow = worksheet.getRow(currentRow);
		const values: string[] = [
			formatTimeTo12Hour(callOut.startTime, timezone),
			formatTimeTo12Hour(callOut.endTime, timezone),
			resolveNurseName(callOut, hospitalData, lastNameFirst),
			resolveCallInTime(callOut).toFormat("ccc, MMM d, h:mm a"),
			callOut.message ?? "",
			resolveCallOffSourceLabel(callOut.deletedSource),
			callOut.removalMessage ?? "",
		];
		values.forEach((value, index) => {
			const cell = excelRow.getCell(index + 1);
			cell.value = value;
			cell.border = THIN_BORDER;
		});
		currentRow += 1;
	}

	return currentRow;
};
